import React, { useState } from 'react';
import { api } from '../lib/api';
import { DEFAULT_TEST_METHODS } from '@gng-ehs/shared/equipment-rules';

interface InlineTestMethodFormProps {
  onCreated: (newMethod: { id: string; name: string }) => void;
  onCancel: () => void;
}

export function InlineTestMethodForm({ onCreated, onCancel }: InlineTestMethodFormProps) {
  const [methodName, setMethodName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async (e?: React.MouseEvent | React.FormEvent) => {
    if (e) e.preventDefault();
    const cleanName = methodName.trim();
    if (!cleanName) {
      setError('Test Method name is required.');
      return;
    }

    // Check if matches built-in standard method
    const builtinMatch = DEFAULT_TEST_METHODS.find(
      (m) => m.toLowerCase() === cleanName.toLowerCase()
    );
    if (builtinMatch) {
      onCreated({ id: builtinMatch, name: builtinMatch });
      return;
    }

    setSaving(true);
    setError('');
    try {
      const res = await api.testMethods.create({ name: cleanName });
      if (res.data) {
        onCreated({ id: res.data.id, name: res.data.name });
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add test method');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 p-2.5 bg-blue-50/80 border border-blue-200 rounded-lg space-y-2">
      <div className="flex items-center justify-between text-xs font-semibold text-blue-900">
        <span>+ Add New Test Method (Point 5)</span>
        <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-700 cursor-pointer">✕</button>
      </div>
      <div className="space-y-2">
        <div>
          <input
            type="text"
            className="input text-xs bg-white w-full"
            placeholder="e.g. Visual inspection & ultrasonic thickness test"
            value={methodName}
            onChange={(e) => setMethodName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(e); }}
            autoFocus
          />
        </div>
        {error && <p className="text-[11px] text-red-600 font-semibold">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="px-2.5 py-1 text-xs text-gray-600 bg-white hover:bg-gray-100 border border-gray-200 rounded-md cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !methodName.trim()}
            className="px-3 py-1 text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 rounded-md shrink-0 disabled:opacity-50 cursor-pointer"
          >
            {saving ? 'Saving Method…' : '+ Add Test Method'}
          </button>
        </div>
      </div>
    </div>
  );
}
